import { getHttpClient } from './http-client';
import { setAccessToken, clearAccessToken, setRefreshToken, clearRefreshToken } from '@safe-minds-ai/utils';
import { Role } from '@prisma/client';

export interface CredentialsPayload {
  email: string;
  password: string;
  popCode?: string;
}

export interface AuthSession {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
  households: Array<{
    id: string;
    name: string;
  }>;
}

export interface SignupPayload {
  email: string;
  password: string;
  parent: {
    firstName: string;
    lastName: string;
  };
  marketingOptIn?: boolean;
  child?: {
    firstName: string;
    lastName: string;
    age: number;
    grade: number;
  };
}

export async function signup(payload: SignupPayload): Promise<{ code?: string; message?: string; redirectUrl?: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ code?: string; message?: string; redirectUrl?: string }>('/auth/signup', {
    email: payload.email,
    password: payload.password,
    role: Role.PARENT,
    parent: payload.parent,
    marketingOptIn: Boolean(payload.marketingOptIn),
    ...(payload.child ? { child: payload.child } : {}),
  });
  return data;
}

export async function login(payload: CredentialsPayload): Promise<void> {
  const client = getHttpClient();
  const { data } = await client.post<{ access_token?: string; accessToken?: string; refresh_token?: string; refreshToken?: string }>(
    '/auth/login',
    payload
  );

  const token = data?.access_token ?? data?.accessToken;
  const refreshToken = data?.refresh_token ?? data?.refreshToken;
  if (token) {
    setAccessToken(token);
  }
  if (refreshToken) {
    setRefreshToken(refreshToken);
  }
}

export async function logout(): Promise<void> {
  const client = getHttpClient();
  await client.post('/auth/logout');
  clearAccessToken();
  clearRefreshToken();
}

export async function requestProofOfParent(): Promise<{ challengeId: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ challengeId: string }>('/auth/proof-of-parent');
  return data;
}

export async function confirmProofOfParent(challengeId: string, code: string): Promise<void> {
  const client = getHttpClient();
  await client.post('/auth/proof-of-parent/verify', { challengeId, code });
}

export async function verifyEmail(token: string): Promise<{ message: string; redirectUrl: string }> {
  const client = getHttpClient();
  const { data } = await client.get<{ message: string; redirectUrl: string }>('/auth/verify-email', {
    params: { token },
  });
  return data;
}

export async function resendVerificationEmail(email: string): Promise<{ message: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ message: string }>('/auth/email-verification/resend', { email });
  return data;
}

export async function resendConsentEmail(email: string): Promise<{ message: string; redirectUrl?: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ message: string; redirectUrl?: string }>('/auth/consent/resend', { email });
  return data;
}

export async function getConsentPreview(token: string): Promise<{ email: string; firstName: string; lastName: string }> {
  const client = getHttpClient();
  const { data } = await client.get<{ email: string; firstName: string; lastName: string }>('/auth/consent/preview', {
    params: { token },
  });
  return data;
}

export interface ConsentPayload {
  token: string;
  relationshipToChild: string;
  children: Array<{
    nickname: string;
    ageRange: string;
  }>;
  isParentOrGuardian: boolean;
  acceptedPrivacyPolicy: boolean;
  consentToDataUse: boolean;
  understandsAiResponses: boolean;
  understandsConsentRequired: boolean;
  understandsRevocation: boolean;
  understandsPrototypeSupervision: boolean;
  signatureFullName: string;
  signatureDate: string;
  marketingOptIn?: boolean;
}

export async function submitConsent(payload: ConsentPayload): Promise<{ redirectUrl: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ redirectUrl: string }>('/auth/consent', payload);
  return data;
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ message: string }>('/auth/password-reset/request', { email });
  return data;
}

export async function completePasswordReset(token: string, password: string): Promise<{ message: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ message: string }>('/auth/password-reset/complete', { token, password });
  return data;
}
